import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { useAuth } from '../auth/auth-store';
import { MemberSelect } from '../ui/UserSelect';
import { listCatalogue } from '../settings/catalogues-api';
import { createCompany, createContact, withScheme } from './records-api';
import { searchEntity, type CompanyRow } from './api';

/**
 * Adding a contact or a company.
 *
 * Until now neither could be created from its own screen: the API had the endpoint, the import
 * could write them, and the only way to get one by hand was to convert a lead. A CRM where you
 * cannot add a company from the companies list is a CRM with a hole in it (context.md §194).
 *
 * One required field each, for the same reason the lead dialog has one: the moment a record is
 * worth writing down is usually the moment somebody has half the information, and a form that
 * demands the rest at that moment is a form people work around by not using it.
 *
 * `<dialog showModal>` rather than a div, so Escape, the focus trap and the backdrop come from the
 * browser. Saving goes straight to the record, because the next thing anybody does with a new one
 * is work it.
 */

function Shell({
  title,
  hint,
  saving,
  submitLabel,
  canSubmit,
  error,
  onSubmit,
  onClose,
  children,
}: {
  title: string;
  hint: string;
  saving: boolean;
  submitLabel: string;
  canSubmit: boolean;
  error: string | null;
  onSubmit: () => void;
  onClose: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = dialog.current;

    if (element && !element.open) element.showModal();
  }, []);

  return (
    <dialog
      ref={dialog}
      className="confirm confirm--wide"
      aria-label={title}
      onCancel={(cancelEvent) => {
        cancelEvent.preventDefault();
        onClose();
      }}
      onClose={onClose}
      onClick={(clickEvent) => {
        if (clickEvent.target === dialog.current) onClose();
      }}
    >
      <form
        onSubmit={(submitEvent) => {
          submitEvent.preventDefault();

          if (canSubmit) onSubmit();
        }}
      >
        <h2>{title}</h2>
        <p className="muted">{hint}</p>

        {children}

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="confirm__actions">
          <button type="button" className="button button--quiet button--compact" onClick={onClose}>
            {t('confirm.cancel')}
          </button>

          <button
            type="submit"
            className="button button--primary button--compact"
            disabled={!canSubmit || saving}
          >
            {saving ? t('work.creating') : submitLabel}
          </button>
        </div>
      </form>
    </dialog>
  );
}

export function NewContactDialog({ onClose }: { onClose: () => void }): React.JSX.Element {
  const navigate = useNavigate();
  const client = useQueryClient();
  const { user } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [ownerId, setOwnerId] = useState(user?.id ?? '');
  const [error, setError] = useState<string | null>(null);

  // The companies somebody might attach them to. Capped, and named on screen rather than by id.
  const companies = useQuery({
    queryKey: ['companies', 'names'],
    queryFn: () => searchEntity<CompanyRow>('companies', { limit: 200 }),
    staleTime: 5 * 60 * 1000,
  });

  const create = useMutation({
    mutationFn: () =>
      createContact({
        firstName: firstName.trim(),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(jobTitle.trim() ? { jobTitle: jobTitle.trim() } : {}),
        ...(companyId ? { companyId } : {}),
        ...(ownerId ? { ownerId } : {}),
      }),
    onSuccess: (contact) => {
      void client.invalidateQueries({ queryKey: ['contacts'] });
      onClose();
      navigate(`/crm/registro/contacts/${contact.id}`);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <Shell
      title={t('crm.newContact')}
      hint={t('crm.newContactHint')}
      saving={create.isPending}
      submitLabel={t('crm.saveContact')}
      canSubmit={firstName.trim() !== ''}
      error={error}
      onSubmit={() => create.mutate()}
      onClose={onClose}
    >
      <div className="form-grid">
        <label className="stack__field">
          <span>
            {t('crm.field.firstName')} <span className="required">*</span>
          </span>
          <input
            value={firstName}
            maxLength={120}
            autoFocus
            onChange={(changeEvent) => setFirstName(changeEvent.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('crm.field.lastName')}</span>
          <input
            value={lastName}
            maxLength={120}
            onChange={(changeEvent) => setLastName(changeEvent.target.value)}
          />
        </label>
      </div>

      <div className="form-grid">
        <label className="stack__field">
          <span>{t('crm.field.email')}</span>
          <input
            type="email"
            value={email}
            maxLength={254}
            onChange={(changeEvent) => setEmail(changeEvent.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('crm.field.phone')}</span>
          <input
            value={phone}
            maxLength={40}
            onChange={(changeEvent) => setPhone(changeEvent.target.value)}
          />
        </label>
      </div>

      <div className="form-grid">
        <label className="stack__field">
          <span>{t('crm.field.jobTitle')}</span>
          <input
            value={jobTitle}
            maxLength={120}
            onChange={(changeEvent) => setJobTitle(changeEvent.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('crm.field.company')}</span>
          <select
            className="select"
            value={companyId}
            onChange={(changeEvent) => setCompanyId(changeEvent.target.value)}
          >
            <option value="">{t('crm.noCompany')}</option>
            {(companies.data?.results ?? []).map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="stack__field">
        <span>{t('crm.owner')}</span>
        <MemberSelect
          value={ownerId || null}
          placeholder={t('crm.unassigned')}
          onChange={(next) => setOwnerId(next ?? '')}
        />
      </div>
    </Shell>
  );
}

export function NewCompanyDialog({ onClose }: { onClose: () => void }): React.JSX.Element {
  const navigate = useNavigate();
  const client = useQueryClient();
  const { user } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [website, setWebsite] = useState('');
  const [industry, setIndustry] = useState('');
  const [statusId, setStatusId] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [ownerId, setOwnerId] = useState(user?.id ?? '');
  const [error, setError] = useState<string | null>(null);

  // The tenant's own catalogues, so the dialog can only offer what the server would accept.
  const statuses = useQuery({
    queryKey: ['catalogue', 'customer-statuses'],
    queryFn: () => listCatalogue('customer-statuses'),
    staleTime: 5 * 60 * 1000,
  });

  const sources = useQuery({
    queryKey: ['catalogue', 'customer-sources'],
    queryFn: () => listCatalogue('customer-sources'),
    staleTime: 5 * 60 * 1000,
  });

  const create = useMutation({
    mutationFn: () =>
      createCompany({
        name: name.trim(),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        // The server wants a URL; `acme.com.br` is what people type.
        ...(website.trim() ? { website: withScheme(website.trim()) } : {}),
        ...(industry.trim() ? { industry: industry.trim() } : {}),
        ...(statusId ? { statusId } : {}),
        ...(sourceId ? { sourceId } : {}),
        ...(ownerId ? { ownerId } : {}),
      }),
    onSuccess: (company) => {
      void client.invalidateQueries({ queryKey: ['companies'] });
      onClose();
      navigate(`/crm/registro/companies/${company.id}`);
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  return (
    <Shell
      title={t('crm.newCompany')}
      hint={t('crm.newCompanyHint')}
      saving={create.isPending}
      submitLabel={t('crm.saveCompany')}
      canSubmit={name.trim() !== ''}
      error={error}
      onSubmit={() => create.mutate()}
      onClose={onClose}
    >
      <label className="stack__field">
        <span>
          {t('crm.field.name')} <span className="required">*</span>
        </span>
        <input
          value={name}
          maxLength={200}
          autoFocus
          onChange={(changeEvent) => setName(changeEvent.target.value)}
        />
      </label>

      <div className="form-grid">
        <label className="stack__field">
          <span>{t('crm.field.email')}</span>
          <input
            type="email"
            value={email}
            maxLength={254}
            onChange={(changeEvent) => setEmail(changeEvent.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('crm.field.phone')}</span>
          <input
            value={phone}
            maxLength={40}
            onChange={(changeEvent) => setPhone(changeEvent.target.value)}
          />
        </label>
      </div>

      <div className="form-grid">
        <label className="stack__field">
          <span>{t('crm.field.website')}</span>
          <input
            value={website}
            maxLength={500}
            placeholder="acme.com.br"
            onChange={(changeEvent) => setWebsite(changeEvent.target.value)}
          />
        </label>

        <label className="stack__field">
          <span>{t('crm.field.industry')}</span>
          <input
            value={industry}
            maxLength={120}
            onChange={(changeEvent) => setIndustry(changeEvent.target.value)}
          />
        </label>
      </div>

      <div className="form-grid">
        <label className="stack__field">
          <span>{t('catalogues.tab.statuses')}</span>
          <select
            className="select"
            value={statusId}
            onChange={(changeEvent) => setStatusId(changeEvent.target.value)}
          >
            <option value="">{t('crm.defaultStatus')}</option>
            {(statuses.data ?? []).map((status) => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>
        </label>

        <label className="stack__field">
          <span>{t('crm.field.source')}</span>
          <select
            className="select"
            value={sourceId}
            onChange={(changeEvent) => setSourceId(changeEvent.target.value)}
          >
            <option value="">{t('crm.noSource')}</option>
            {(sources.data ?? []).map((source) => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="stack__field">
        <span>{t('crm.owner')}</span>
        <MemberSelect
          value={ownerId || null}
          placeholder={t('crm.unassigned')}
          onChange={(next) => setOwnerId(next ?? '')}
        />
      </div>
    </Shell>
  );
}
